import { useMemo } from 'react';
import { useRealizedGainLossQuery } from '../api/useRealizedGainLossQuery';
import { useRouteParams } from './useRouteParams';
import { useTransactionFilters } from './useTransactionFilters';
import { Cell, TableData } from './useUnrealizedGainLoss';

export const useRealizedGainLoss = () => {
  const { accountId, groupId, clientId } = useRouteParams();

  const transactionFilters = useTransactionFilters();

  const queryInfo = useRealizedGainLossQuery({
    clientId,
    accountId,
    groupId,
    period: transactionFilters.period.value,
    startDate: transactionFilters.period.dates[0],
    endDate: transactionFilters.period.dates[1],
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const tableData = {} as TableData;

    tableData.headers = [
      {
        text: 'Name',
        isMobile: true,
      },
      {
        text: 'Account Number',
      },
      {
        text: 'Ticker',
      },
      {
        text: 'Units',
      },
      {
        text: 'Price',
      },
      {
        text: 'Cost Basis',
      },

      {
        text: 'Short Term Gain Loss',
      },
      {
        text: 'Long Term Gain Loss',
      },
      {
        text: 'Gain Loss',
        isMobile: true,
      },
      {
        text: 'Acquired Date',
      },
      {
        text: 'Sold Date',
      },
      {
        text: 'Holding Period',
      },
      {
        text: 'Proceeds',
      },
    ];

    tableData.rows = queryInfo.data.map(
      (
        {
          name,
          accountNumber,
          proceeds,
          costBasis,
          shortTerm,
          longTerm,
          gainLoss,
          holdingPeriod,
          positions,
        },
        index,
      ) => ({
        id: index.toString(),
        cells: [
          { text: name ?? '', align: 'left', isMobile: true },
          { text: accountNumber ?? '', align: 'right' },
          { text: '', align: 'right' },
          {
            text: '',
            type: 'number',
            align: 'right',
          },
          {
            text: '',
            type: 'number',
            align: 'right',
          },

          {
            text: costBasis ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: shortTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: longTerm ?? '',
            type: 'currency',
            align: 'right',
          },
          {
            text: gainLoss ?? '',
            type: 'currency',
            align: 'right',
            isMobile: true,
          },
          {
            text: '',
            align: 'right',
          },
          {
            text: '',
            align: 'right',
          },
          {
            text: holdingPeriod ?? '',
            align: 'left',
          },
          {
            text: proceeds ?? '',
            type: 'currency',
            align: 'right',
          },
        ] as Cell[],
        childRows: [
          ...positions.map(
            (
              {
                name: posName,
                accountNumber: posAccountNumber,
                ticker,
                units: posUnits,
                price: posPrice,
                proceeds: posProceeds,
                costBasis: posCostBasis,
                shortTerm: posShortTerm,
                longTerm: posLongTerm,
                gainLoss: posGainLoss,
                taxLotDetails,
              },
              posIndex,
            ) => ({
              id: posIndex.toString(),
              cells: [
                { text: posName ?? '', align: 'left' },
                { text: posAccountNumber ?? '', align: 'right' },
                { text: ticker ?? '', align: 'right' },
                { text: posUnits ?? '', align: 'right', type: 'number' },
                { text: posPrice ?? '', align: 'right', type: 'currency' },
                { text: posCostBasis ?? '', align: 'right', type: 'currency' },
                { text: posShortTerm ?? '', align: 'right', type: 'currency' },
                { text: posLongTerm ?? '', align: 'right', type: 'currency' },
                { text: posGainLoss ?? '', align: 'right', type: 'currency' },
                { text: '', align: 'left' },
                { text: '', align: 'left' },
                { text: '', align: 'left' },
                { text: posProceeds ?? '', align: 'right', type: 'currency' },
              ] as Cell[],
              lotDetailsRows: [
                ...taxLotDetails.map(
                  (
                    {
                      acquiredDate,
                      costBasis: lotCostBasis,
                      holdingPeriod: lotHoldingPeriod,
                      longTerm: lotLongTerm,
                      proceeds: lotProceeds,
                      price: lotPrice,
                      sellDate,
                      shortTerm: lotShortTerm,
                      total,
                      units: lotUnits,
                    },
                    lotIndex,
                  ) => ({
                    id: lotIndex.toString(),
                    cells: [
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: '', align: 'right' },
                      { text: lotUnits ?? '', align: 'right', type: 'number' },
                      { text: lotPrice ?? '', align: 'right', type: 'currency' },
                      { text: lotCostBasis ?? '', align: 'right', type: 'currency' },
                      { text: lotShortTerm ?? '', align: 'right', type: 'currency' },
                      { text: lotLongTerm ?? '', align: 'right', type: 'currency' },
                      { text: total ?? '', align: 'right', type: 'currency' },
                      { text: acquiredDate ?? '', align: 'left', type: 'date' },
                      { text: sellDate ?? '', align: 'left', type: 'date' },
                      { text: lotHoldingPeriod ?? '', align: 'left' },
                      { text: lotProceeds ?? '', align: 'right', type: 'currency' },
                    ] as Cell[],
                  }),
                ),
              ],
            }),
          ),
        ],
      }),
    );

    const totals = queryInfo.data.reduce(
      (acc, item) => {
        acc.shortTerm += item.shortTerm;
        acc.longTerm += item.longTerm;
        acc.total += item.gainLoss;
        return acc;
      },
      { shortTerm: 0, longTerm: 0, total: 0 },
    );

    return { tableData, totals, gainLoss: queryInfo.data };
  }, [queryInfo]);

  return { ...queryInfo, data };
};
